import { useState, useEffect, useCallback } from "react";
import { CheckCircle, AlertCircle, Send, RefreshCw, Euro, Clock, X, Check, Edit2, Trash2, Plus, FileText, Receipt, Download, Calculator, RotateCcw, Archive } from "lucide-react";
import { salesAPI, settingsAPI, reportsAPI, cashMovementsAPI, returnsAPI } from "../services/api";

const PAY_METHODS = { cash: "Numerário", card: "Multibanco/Cartão", mbway: "MBWay", multibanco: "Multibanco/Cartão", transfer: "Transferência", account_current: "Conta Corrente", voucher: "Voucher", other: "Outro" };
const normalizeFiscalId = (value) => String(value || "").toUpperCase().replace(/\s+/g, "").replace(/^PT/, "");
const isValidPortugueseNif = (value) => {
  const nif = normalizeFiscalId(value);
  if (!nif) return true;
  if (!/^\d{9}$/.test(nif)) return false;
  const sum = nif.slice(0, 8).split("").reduce((total, digit, index) => total + Number(digit) * (9 - index), 0);
  const remainder = 11 - (sum % 11);
  const checkDigit = remainder >= 10 ? 0 : remainder;
  return checkDigit === Number(nif[8]);
};
const localBusinessDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function DayClosePage() {
  const [sales, setSales] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fiscalizing, setFiscalizing] = useState(false);
  const [excludingSelected, setExcludingSelected] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [retryingReturnId, setRetryingReturnId] = useState("");
  const [results, setResults] = useState(null);
  const [activeRegister, setActiveRegister] = useState(null);
  const [dateFilter, setDateFilter] = useState(localBusinessDate);
  const [viewMode, setViewMode] = useState("day"); // "day" | "all_pending"
  const [editSale, setEditSale] = useState(null); // venda a editar
  const [editForm, setEditForm] = useState(null);
  const [prepaymentsSummary, setPrepaymentsSummary] = useState(null);
  const [cashSummary, setCashSummary] = useState(null);
  const [openCashSessions, setOpenCashSessions] = useState([]);
  const [closingCashOperator, setClosingCashOperator] = useState("");
  const [consolidatedCash, setConsolidatedCash] = useState(null);
  const [showConsolidatedClose, setShowConsolidatedClose] = useState(false);
  const [consolidatedForm, setConsolidatedForm] = useState(null);
  const [closingConsolidated, setClosingConsolidated] = useState(false);
  const [returnsRecon, setReturnsRecon] = useState(null);
  const [returnsReconLoading, setReturnsReconLoading] = useState(false);
  const [excludedSales, setExcludedSales] = useState([]);
  const [showExcludedSales, setShowExcludedSales] = useState(false);
  const [expandedExcludedId, setExpandedExcludedId] = useState("");
  const [restoringExcludedId, setRestoringExcludedId] = useState("");


  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const settingsRes = await settingsAPI.getVendusActive();
      const data = settingsRes.data || {};
      setSettings(data);

      const locations = Array.isArray(data?.vendus_locations) ? data.vendus_locations : [];
      const loc =
        data?.active_location ||
        locations.find((l) => l?.is_active === true || String(l?.is_active || '').toLowerCase() === 'true') ||
        locations.find((l) => l?.api_key) ||
        null;

      setActiveRegister(loc || null);
    } catch (e) {
      console.error("Settings error", e);
      setSettings({});
      setActiveRegister(null);
    }

    try {
      const prepaymentsRes = await reportsAPI.getPrepaymentsSummary();
      setPrepaymentsSummary(prepaymentsRes?.data?.summary || null);
    } catch (e) {
      console.error("Prepayments summary error", e);
      setPrepaymentsSummary(null);
    }

    try {
      const cashRes = await cashMovementsAPI.getSessionSummary({ date: dateFilter });
      setCashSummary(cashRes?.data || null);
    } catch (e) {
      console.error("Cash session summary error", e);
      setCashSummary(null);
    }

    try {
      const openRes = await cashMovementsAPI.getOpenSessions();
      setOpenCashSessions(openRes?.data?.sessions || []);
    } catch (e) {
      console.error("Open cash sessions error", e);
      setOpenCashSessions([]);
    }

    try {
      const consolidatedRes = await cashMovementsAPI.getConsolidatedSummary(dateFilter);
      setConsolidatedCash(consolidatedRes?.data || null);
    } catch (e) {
      console.error("Consolidated cash summary error", e);
      setConsolidatedCash(null);
    }

    try {
      setReturnsReconLoading(true);
      const reconParams = viewMode === "day"
        ? { start_date: dateFilter, end_date: dateFilter }
        : {};
      const reconRes = await returnsAPI.getReconciliationReport(reconParams);
      setReturnsRecon(reconRes?.data || null);
    } catch (e) {
      console.error("Returns reconciliation error", e);
      setReturnsRecon(null);
    } finally {
      setReturnsReconLoading(false);
    }

    try {
      // O backend filtra por estado. A seleção fiscal é feita abaixo pelo
      // estado real do documento, para incluir também tentativas fiscais que
      // falharam antes de obter um ID Vendus.
      const salesRes = await salesAPI.getAll({ status: "completed" });
      const allSales = salesRes.data || [];

      const pending = allSales.filter((s) => {
        const saleDay = String(s?.completed_at || "").slice(0, 10);

        return (
          ["internal", "fiscal"].includes(String(s?.invoice_type || "").toLowerCase()) &&
          String(s?.status || "").toLowerCase() === "completed" &&
          (viewMode === "all_pending" || saleDay === dateFilter) &&
          !s?.vendus_invoice_id &&
          !s?.document_id &&
          !s?.vendus_document_id &&
          !s?.exclude_from_vendus
        );
      });

      setSales(pending);
      setSelected((prev) => prev.filter((id) => pending.some((s) => s.id === id)));
    } catch (e) {
      console.error("Sales error", e);
    }

    try {
      const excludedRes = await salesAPI.getRecoverableVendusExclusions();
      setExcludedSales(excludedRes?.data?.sales || []);
    } catch (e) {
      console.error("Excluded sales error", e);
      setExcludedSales([]);
    }

    setLoading(false);
  }, [dateFilter, viewMode]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openEdit = (sale) => {
    setEditSale(sale);
    const fiscalData = sale.day_close_fiscal_override || sale;
    setEditForm({
      customer_nif: sale.day_close_fiscal_override?.customer_nif ?? "",
      customer_name: fiscalData.customer_name || "Consumidor Final",
      payment_method: fiscalData.payment_method || "cash",
      notes: fiscalData.notes || "",
      items: (fiscalData.items || []).map(i => ({ ...i }))
    });
  };

  const recalcTotals = (items) => {
    let subtotal = 0, vat_total = 0;
    for (const item of items) {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      const disc = parseFloat(item.discount_percent) || 0;
      const parsedVat = parseFloat(item.vat_rate);
      const vat = Number.isFinite(parsedVat) ? parsedVat : 23;
      const line_total = qty * price * (1 - disc / 100);
      const line_net = line_total / (1 + vat / 100);
      subtotal += line_net;
      vat_total += line_total - line_net;
    }
    return { subtotal: Math.round(subtotal * 100) / 100, vat_total: Math.round(vat_total * 100) / 100, total: Math.round((subtotal + vat_total) * 100) / 100 };
  };

  const saveEdit = async () => {
    const normalizedNif = normalizeFiscalId(editForm.customer_nif);
    if (!isValidPortugueseNif(normalizedNif)) {
      alert("NIF inválido. Confirme os 9 algarismos antes de enviar ao Vendus.");
      return;
    }
    if (normalizedNif && normalizedNif !== "999999990" && !String(editForm.customer_name || "").trim()) {
      alert("Indique o nome do cliente associado ao NIF.");
      return;
    }
    const totals = recalcTotals(editForm.items);
    setSavingEdit(true);
    try {
      const fiscalEdit = { ...editForm, customer_nif: normalizedNif, ...totals };
      const res = await salesAPI.updateDayClose(editSale.id, fiscalEdit);
      const updatedSale = res?.data || {
        ...editSale,
        day_close_fiscal_override: fiscalEdit,
        day_close_edited: true
      };
      setSales(prev => prev.map(s => s.id === editSale.id ? updatedSale : s));
      setEditSale(null);
      setEditForm(null);
    } catch (e) {
      alert("Erro: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAll = () => setSelected(selected.length === sales.length ? [] : sales.map(s => s.id));
  const totalSelected = sales.filter(s => selected.includes(s.id)).reduce((sum, s) => sum + (s.total || 0), 0);

  const supervisorCloseCash = async (session) => {
    const countedRaw = window.prompt(
      `Fechar a caixa de ${session.operator}.\nNumerário esperado: ${Number(session.expected_cash || 0).toFixed(2)}€\n\nIndique o numerário efetivamente contado:`,
      Number(session.expected_cash || 0).toFixed(2)
    );
    if (countedRaw === null) return;
    const countedCash = Number(String(countedRaw).replace(',', '.'));
    if (!Number.isFinite(countedCash) || countedCash < 0) {
      alert('Indique um valor válido para o numerário contado.');
      return;
    }
    if (!window.confirm(`Confirma o fecho da caixa de ${session.operator} com ${countedCash.toFixed(2)}€ contados?`)) return;
    setClosingCashOperator(session.operator_id);
    try {
      await cashMovementsAPI.supervisorClose(session.operator_id, { counted_cash: countedCash });
      await loadAll();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally {
      setClosingCashOperator('');
    }
  };

  const openConsolidatedClose = () => {
    const expected = consolidatedCash?.expected_receipts || {};
    const expectedCashTotal = Number(consolidatedCash?.totals?.expected_cash || 0)
      + Number(consolidatedCash?.prepayments?.by_method?.cash?.received || 0);
    setConsolidatedForm({
      counted_cash_total: expectedCashTotal.toFixed(2),
      tpas: [{ name: 'TPA 1', amount: Number(expected.card_total || 0).toFixed(2) }],
      actual_methods: {
        mbway: Number(expected.mbway || 0).toFixed(2),
        transfer: Number(expected.transfer || 0).toFixed(2),
        voucher: Number(expected.voucher || 0).toFixed(2),
        account_current: Number(expected.account_current || 0).toFixed(2),
        other: Number(expected.other || 0).toFixed(2),
      },
      note: '',
    });
    setShowConsolidatedClose(true);
  };

  const closeConsolidatedCash = async () => {
    const sessions = consolidatedCash?.sessions || [];
    if (!sessions.length || !consolidatedForm) return;
    const countedCashTotal = Number(String(consolidatedForm.counted_cash_total ?? '').replace(',', '.'));
    if (!Number.isFinite(countedCashTotal) || countedCashTotal < 0) {
      alert('Indique o total de dinheiro contado em todas as caixas.');
      return;
    }
    if (!window.confirm(`Confirma o fecho conjunto de ${sessions.length} caixa(s)? Esta ação encerra todas as sessões indicadas.`)) return;
    setClosingConsolidated(true);
    try {
      const payload = {
        date: dateFilter,
        open_ids: sessions.map(row => row.open_id),
        counted_cash_total: countedCashTotal,
        tpas: (consolidatedForm.tpas || []).map((row, index) => ({ name: row.name || `TPA ${index + 1}`, amount: Number(String(row.amount || 0).replace(',', '.')) })),
        actual_methods: Object.fromEntries(Object.entries(consolidatedForm.actual_methods || {}).map(([key, value]) => [key, Number(String(value || 0).replace(',', '.'))])),
        note: consolidatedForm.note || '',
      };
      const res = await cashMovementsAPI.consolidatedClose(payload);
      alert(res?.data?.message || 'Fecho consolidado concluído.');
      setShowConsolidatedClose(false);
      setConsolidatedForm(null);
      await loadAll();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally {
      setClosingConsolidated(false);
    }
  };

  const consolidatedPreview = (() => {
    if (!consolidatedCash || !consolidatedForm) return null;
    const expected = consolidatedCash.expected_receipts || {};
    const prepaymentCash = Number(consolidatedCash.prepayments?.by_method?.cash?.received || 0);
    const expectedByMethod = {
      cash: Number(consolidatedCash.totals?.expected_cash || 0) + prepaymentCash,
      card_total: Number(expected.card_total || 0),
      mbway: Number(expected.mbway || 0),
      transfer: Number(expected.transfer || 0),
      voucher: Number(expected.voucher || 0),
      account_current: Number(expected.account_current || 0),
      other: Number(expected.other || 0),
    };
    const actualByMethod = {
      cash: Number(String(consolidatedForm.counted_cash_total || 0).replace(',', '.')) || 0,
      card_total: (consolidatedForm.tpas || []).reduce((sum, row) => sum + (Number(String(row.amount || 0).replace(',', '.')) || 0), 0),
      ...Object.fromEntries(Object.entries(consolidatedForm.actual_methods || {}).map(([key, value]) => [key, Number(String(value || 0).replace(',', '.')) || 0])),
    };
    const comparison = Object.fromEntries(Object.keys(expectedByMethod).map(key => [key, {
      expected: expectedByMethod[key],
      actual: actualByMethod[key] || 0,
      difference: (actualByMethod[key] || 0) - expectedByMethod[key],
    }]));
    const totalDifference = Object.values(comparison).reduce((sum, row) => sum + row.difference, 0);
    return { expectedByMethod, actualByMethod, comparison, totalDifference };
  })();

  const fiscalizeBatch = async () => {
    if (!selected.length || !activeRegister) return;
    setFiscalizing(true);
    setResults(null);
    try {
      // Guarda edições no backend antes de faturar
      for (const sid of selected) {
        const sale = sales.find(s => s.id === sid);
        if (sale?._edited) {
          await salesAPI.update?.(sid, {
            customer_nif: sale.customer_nif,
            customer_name: sale.customer_name,
            payment_method: sale.payment_method,
            notes: sale.notes,
            items: sale.items,
            subtotal: sale.subtotal,
            vat_total: sale.vat_total,
            total: sale.total
          }).catch(() => {});
        }
      }
      const res = await salesAPI.fiscalizeBatch(selected, activeRegister.register_id);
      setResults(res.data);
      await loadAll();
      setSelected([]);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setResults({
          fiscalized: Number(detail.fiscalized || 0),
          errors: Number(detail.errors || detail.error_details?.length || 0),
          results: detail.results || [],
          error_details: detail.error_details || []
        });
        alert("Erro: " + (detail.message || "Não foi possível emitir as faturas no Vendus"));
      } else {
        alert("Erro: " + (detail || e.message));
      }
    } finally { setFiscalizing(false); }
  };

  const excludeFromVendus = async (sale) => {
    const ok = window.confirm(
      `Tens a certeza que queres excluir a venda ${sale.ncp_number || sale.id} do envio ao Vendus?\n\nA venda mantém-se nos registos internos, mas deixa de aparecer pendente no fecho do dia.`
    );

    if (!ok) return;

    try {
      await salesAPI.excludeFromVendus(sale.id);
      setSelected((prev) => prev.filter((id) => id !== sale.id));
      await loadAll();
    } catch (e) {
      alert("Erro: " + (e.response?.data?.detail || e.message));
    }
  };

  const excludeSelectedFromVendus = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Excluir ${selected.length} venda(s) selecionada(s) do envio ao Vendus?\n\nAs vendas mantêm-se no histórico interno.`)) return;
    setExcludingSelected(true);
    try {
      const res = await salesAPI.excludeFromVendusBatch(selected);
      const errors = Number(res?.data?.errors || 0);
      if (errors) alert(`${res.data.excluded} venda(s) excluída(s); ${errors} não puderam ser excluídas.`);
      setSelected([]);
      await loadAll();
    } catch (e) {
      alert("Erro: " + (e.response?.data?.detail || e.message));
    } finally {
      setExcludingSelected(false);
    }
  };

  const restoreExcludedSale = async (sale) => {
    const label = sale.ncp_number || sale.id;
    if (!window.confirm(`Recuperar a venda ${label} e voltar a colocá-la nas pendentes do Fecho do Dia?`)) return;
    setRestoringExcludedId(sale.id);
    try {
      await salesAPI.restoreToDayClose(sale.id);
      setExpandedExcludedId("");
      await loadAll();
      alert(`Venda ${label} recuperada com sucesso.`);
    } catch (e) {
      alert("Erro: " + (e.response?.data?.detail || e.message));
    } finally {
      setRestoringExcludedId("");
    }
  };

  const exclusionTimeLeft = (sale) => {
    const hours = Math.max(0, Number(sale.hours_remaining || 0));
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return days > 0 ? `${days}d ${remainingHours}h` : `${remainingHours}h`;
  };

  const retryCreditNote = async (row) => {
    if (!window.confirm(`Emitir agora a nota de crédito Vendus da devolução ${row.return_number}?`)) return;
    setRetryingReturnId(row.return_id);
    try {
      const res = await returnsAPI.retryVendusCreditNote(row.return_id);
      alert(res?.data?.return?.vendus_credit_note_number ? `Nota de crédito ${res.data.return.vendus_credit_note_number} emitida.` : 'Nota de crédito emitida.');
      await loadAll();
    } catch (e) {
      alert("Erro: " + (e.response?.data?.detail || e.message));
    } finally {
      setRetryingReturnId("");
    }
  };

  const convertDiscountToPrice = (idx, forcedPercent = null) => {
    const items = [...editForm.items];
    const item = items[idx];
    const percent = forcedPercent === null ? Number(item.discount_percent || 0) : Number(forcedPercent || 0);
    const newPrice = Math.round(Number(item.unit_price || 0) * (1 - percent / 100) * 100) / 100;
    items[idx] = { ...item, unit_price: newPrice, discount_percent: 0, discount: 0, total: Math.round(Number(item.quantity || 0) * newPrice * 100) / 100 };
    setEditForm(form => ({ ...form, items }));
  };

  const groupedSales = sales.reduce((acc, sale) => {
    const day = String(sale?.completed_at || "").slice(0, 10) || "Sem data";
    if (!acc[day]) acc[day] = [];
    acc[day].push(sale);
    return acc;
  }, {});

  const reconciliationItems = returnsRecon?.items || [];
  const reconciliationSummary = returnsRecon?.summary || {};
  const reconciliationPending = reconciliationItems.filter(
    (item) => String(item?.fiscal_reconciliation_status || "").toLowerCase() === "pendente_nc_manual"
  );

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReconCSV = async () => {
    try {
      const params = viewMode === "day" ? { start_date: dateFilter, end_date: dateFilter } : {};
      const res = await returnsAPI.getReconciliationReportCSV(params);
      downloadBlob(res.data, `reconciliacao_devolucoes_${viewMode === "day" ? dateFilter : "todas"}.csv`);
    } catch (e) {
      alert("Erro ao exportar CSV: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleDownloadReconPDF = async () => {
    try {
      const params = viewMode === "day" ? { start_date: dateFilter, end_date: dateFilter } : {};
      const res = await returnsAPI.getReconciliationReportPDF(params);
      downloadBlob(res.data, `reconciliacao_devolucoes_${viewMode === "day" ? dateFilter : "todas"}.pdf`);
    } catch (e) {
      alert("Erro ao exportar PDF A4: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleDownloadReconReceiptPDF = async () => {
    try {
      const params = viewMode === "day" ? { start_date: dateFilter, end_date: dateFilter } : {};
      const res = await returnsAPI.getReconciliationReportReceiptPDF(params);
      downloadBlob(res.data, `reconciliacao_devolucoes_talao_${viewMode === "day" ? dateFilter : "todas"}.pdf`);
    } catch (e) {
      alert("Erro ao exportar talão 80mm: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Fecho de Dia</h1>
          <p className="text-gray-400 text-sm mt-0.5">Revê e edita cada venda antes de enviar ao Vendus</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowExcludedSales(true)}
            className="relative bg-amber-900/40 hover:bg-amber-800/60 border border-amber-700 text-amber-200 px-3 py-2 rounded-xl text-sm flex items-center gap-2"
          >
            <Archive className="w-4 h-4" /> Excluídas (7 dias)
            {excludedSales.length > 0 && <span className="min-w-5 h-5 px-1 rounded-full bg-amber-500 text-slate-950 text-xs font-bold flex items-center justify-center">{excludedSales.length}</span>}
          </button>
          <select
            value={viewMode}
            onChange={e => setViewMode(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-green-600"
          >
            <option value="day">Dia selecionado</option>
            <option value="all_pending">Todas pendentes</option>
          </select>

          <input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            disabled={viewMode !== "day"}
            className="bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-green-600 disabled:opacity-50"
          />

          <button onClick={loadAll} className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-xl">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!activeRegister && (
        <div className="bg-yellow-900/20 border border-yellow-800 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-400 shrink-0" />
          <p className="text-yellow-300 text-sm">Vendus não configurado — vai a <strong>Configurações → Vendus</strong> para activar.</p>
        </div>
      )}

      {openCashSessions.length > 0 && (
        <div className="bg-rose-950/40 border border-rose-700 rounded-xl p-4">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
            <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-rose-200 font-semibold">Existem {openCashSessions.length} caixa(s) ainda aberta(s)</h2>
              <p className="text-rose-300/80 text-sm">O fecho do dia deve ser confirmado apenas depois de todas as caixas serem encerradas.</p>
            </div>
            </div>
            {Number(consolidatedCash?.session_count || 0) > 0 && (
              <button onClick={openConsolidatedClose} className="shrink-0 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold flex items-center justify-center gap-2">
                <Calculator className="w-4 h-4" /> Fechar {consolidatedCash.session_count} caixas em conjunto
              </button>
            )}
          </div>
          <div className="space-y-2">
            {openCashSessions.map((session) => (
              <div key={session.operator_id} className="bg-slate-900/80 border border-slate-700 rounded-lg p-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="text-sm">
                  <p className="text-white font-semibold">{session.operator}</p>
                  <p className="text-slate-400">
                    Aberta em {session.open_date} · {session.sales_count} venda(s) · esperado {Number(session.expected_cash || 0).toFixed(2)}€
                    {session.is_previous_day ? <span className="text-rose-400 font-semibold"> · DIA ANTERIOR</span> : null}
                  </p>
                </div>
                <button
                  onClick={() => supervisorCloseCash(session)}
                  disabled={closingCashOperator === session.operator_id}
                  className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-semibold"
                >
                  {closingCashOperator === session.operator_id ? 'A fechar…' : 'Fechar e contabilizar'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-yellow-400" /><p className="text-gray-400 text-xs">Pendentes</p></div>
          <p className="text-2xl font-bold text-yellow-400">{sales.length}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Euro className="w-4 h-4 text-white" /><p className="text-gray-400 text-xs">Total Pendente</p></div>
          <p className="text-2xl font-bold text-white">{sales.reduce((s, v) => s + (v.total || 0), 0).toFixed(2)}€</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Check className="w-4 h-4 text-green-400" /><p className="text-gray-400 text-xs">Selecionadas</p></div>
          <p className="text-2xl font-bold text-green-400">{selected.length} · {totalSelected.toFixed(2)}€</p>
        </div>
      </div>

      {cashSummary && (
        <div className="bg-slate-900/70 border border-slate-700 rounded-xl p-4">
          <div className="mb-3">
            <h2 className="text-white font-semibold">Resumo real de caixa</h2>
            <p className="text-slate-400 text-sm">Valores da sessão de caixa do dia selecionado. O numerário esperado mantém-se separado dos pré-pagamentos em aberto.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Abertura</p>
              <p className="text-xl font-bold text-white">{Number(cashSummary.opening || 0).toFixed(2)}€</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Numerário esperado</p>
              <p className="text-xl font-bold text-emerald-400">{Number(cashSummary.expected_cash || 0).toFixed(2)}€</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Numerário contado</p>
              <p className="text-xl font-bold text-white">{Number(cashSummary.counted_cash || 0).toFixed(2)}€</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Diferença</p>
              <p className={`text-xl font-bold ${Number(cashSummary.difference || 0) === 0 ? 'text-white' : Number(cashSummary.difference || 0) > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {Number(cashSummary.difference || 0).toFixed(2)}€
              </p>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-slate-400">Dinheiro</p>
              <p className="text-white font-semibold">{Number(cashSummary.cash || 0).toFixed(2)}€</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-slate-400">Multibanco/Cartão</p>
              <p className="text-white font-semibold">{(Number(cashSummary.card || 0) + Number(cashSummary.multibanco || 0)).toFixed(2)}€</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-slate-400">MBWay</p>
              <p className="text-white font-semibold">{Number(cashSummary.mbway || 0).toFixed(2)}€</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-slate-400">Conta Corrente</p>
              <p className="text-white font-semibold">{Number(cashSummary.account_current || 0).toFixed(2)}€</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-slate-400">Entradas manuais</p>
              <p className="text-white font-semibold">{Number(cashSummary.cash_in || 0).toFixed(2)}€</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-slate-400">Saídas manuais</p>
              <p className="text-white font-semibold">{Number(cashSummary.cash_out || 0).toFixed(2)}€</p>
            </div>
          </div>
        </div>
      )}

      {prepaymentsSummary && (
        <div className="bg-slate-900/70 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div>
              <h2 className="text-white font-semibold">Resumo de pré-pagamentos</h2>
              <p className="text-slate-400 text-sm">Adiantamentos recebidos e ainda não consumidos. Este bloco não altera diretamente o numerário esperado da caixa.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Recebido encomendas</p>
              <p className="text-xl font-bold text-white">{Number(prepaymentsSummary.received_customer_orders || 0).toFixed(2)}€</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Recebido consultas</p>
              <p className="text-xl font-bold text-white">{Number(prepaymentsSummary.received_consultations || 0).toFixed(2)}€</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Consumido em vendas</p>
              <p className="text-xl font-bold text-emerald-400">{Number(prepaymentsSummary.consumed_linked_customer_orders ?? prepaymentsSummary.consumed_in_sales ?? 0).toFixed(2)}€</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Saldo aberto</p>
              <p className="text-xl font-bold text-amber-400">{Number(prepaymentsSummary.open_total_estimated || 0).toFixed(2)}€</p>
            </div>
          </div>

          <div className="mt-3 text-xs text-slate-400 flex flex-wrap gap-x-5 gap-y-1">
            <span>Total recebido: <strong className="text-slate-200">{Number(prepaymentsSummary.received_total || 0).toFixed(2)}€</strong></span>
            <span>Consumidos ligados: <strong className="text-slate-200">{prepaymentsSummary.linked_consumed_count || 0}</strong></span>
            <span>Consumidos sem ligação: <strong className="text-slate-200">{prepaymentsSummary.unlinked_consumed_count || 0}</strong></span>
          </div>

          {prepaymentsSummary?.by_method && (
            <div className="mt-4 border-t border-gray-700 pt-4">
              <h3 className="text-white font-semibold mb-3">Passivo por método</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-sm">
                {[
                  ['cash', 'Dinheiro'],
                  ['card', 'Cartão'],
                  ['mbway', 'MBWay'],
                  ['multibanco', 'Multibanco'],
                  ['transfer', 'Transferência'],
                    ['account_current', 'Conta Corrente'],
                  ['voucher', 'Voucher'],
                  ['other', 'Outro'],
                ].map(([key, label]) => {
                  const row = prepaymentsSummary.by_method?.[key] || {};
                  return (
                    <div key={key} className="bg-gray-900/60 border border-gray-700 rounded-xl p-3 space-y-1">
                      <p className="text-slate-300 font-medium">{label}</p>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Recebido</span>
                        <span className="text-white font-semibold">{Number(row.received || 0).toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Consumido</span>
                        <span className="text-emerald-400 font-semibold">{Number(row.consumed || 0).toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Em aberto</span>
                        <span className="text-amber-400 font-semibold">{Number(row.open || 0).toFixed(2)}€</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {results && (
        <div className={`rounded-xl p-4 border ${results.errors > 0 ? "bg-yellow-900/20 border-yellow-800" : "bg-green-900/20 border-green-800"}`}>
          <p className="text-white font-medium mb-2">{results.fiscalized} faturas emitidas{results.errors > 0 ? `, ${results.errors} erros` : " ✅"}</p>
          {results.results?.map((r, i) => <p key={i} className="text-green-300 text-sm">✅ {r.document_number}</p>)}
          {results.error_details?.map((e, i) => <p key={i} className="text-red-300 text-sm">❌ {e.error}</p>)}
        </div>
      )}

      <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3 border-b border-gray-700">
          <div>
            <h2 className="text-white font-semibold">Reconciliação fiscal de devoluções</h2>
            <p className="text-gray-400 text-sm">
              Aqui tens as devoluções já processadas na app que ficaram com nota de crédito manual pendente no Vendus.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleDownloadReconCSV}
              className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-xs flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>

            <button
              onClick={handleDownloadReconPDF}
              className="bg-blue-700 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-xs flex items-center gap-2"
            >
              <FileText className="w-3.5 h-3.5" />
              PDF A4
            </button>

            <button
              onClick={handleDownloadReconReceiptPDF}
              className="bg-emerald-700 hover:bg-emerald-600 text-white px-3 py-2 rounded-lg text-xs flex items-center gap-2"
            >
              <Receipt className="w-3.5 h-3.5" />
              Talão 80mm
            </button>
          </div>
        </div>

        <div className="p-4 border-b border-gray-700 bg-gray-900/40">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Devoluções no período</p>
              <p className="text-xl font-bold text-white">{Number(reconciliationSummary.total_returns || 0)}</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Total devolvido</p>
              <p className="text-xl font-bold text-white">{Number(reconciliationSummary.total_refund || 0).toFixed(2)}€</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Total em vouchers</p>
              <p className="text-xl font-bold text-white">{Number(reconciliationSummary.total_vouchers || 0).toFixed(2)}€</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">NC fiscal pendente</p>
              <p className="text-xl font-bold text-amber-400">{Number(reconciliationSummary.pending_manual_nc || 0)}</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">NC automática</p>
              <p className="text-xl font-bold text-emerald-400">{Number(reconciliationSummary.auto_nc || 0)}</p>
            </div>
          </div>
        </div>

        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              {["Data","Devolução","Cliente","Venda app","Fatura Vendus","Voucher","Estado","Dif.","Nota curta","Ação"].map(h => (
                <th key={h} className="text-left text-gray-400 text-xs font-medium px-3 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {returnsReconLoading ? (
              <tr>
                <td colSpan={10} className="text-center text-gray-500 py-8 text-sm">A carregar reconciliação...</td>
              </tr>
            ) : reconciliationPending.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center py-8 text-sm text-gray-400">
                  Não existem devoluções fiscais com NC pendente neste período.
                </td>
              </tr>
            ) : (
              reconciliationPending.map((row) => (
                <tr key={row.return_id} className="border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors">
                  <td className="px-3 py-3 text-gray-300 text-sm">{String(row.created_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-3 text-white text-sm font-medium">{row.return_number || "—"}</td>
                  <td className="px-3 py-3 text-gray-300 text-sm">{row.customer_name || "—"}</td>
                  <td className="px-3 py-3 text-gray-300 text-sm">{row.sale_ncp_number || "—"}</td>
                  <td className="px-3 py-3 text-gray-300 text-sm">{row.vendus_invoice_number || "—"}</td>
                  <td className="px-3 py-3 text-gray-300 text-sm">{row.voucher_code || "—"}</td>
                  <td className="px-3 py-3">
                    <span className="text-xs bg-amber-900/40 text-amber-300 border border-amber-800 px-2 py-0.5 rounded-full">
                      {row.fiscal_reconciliation_status_label || row.fiscal_reconciliation_status || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-white text-sm font-medium">{Number(row.difference_between_customer_paid_and_vendus || 0).toFixed(2)}€</td>
                  <td className="px-3 py-3 text-gray-400 text-xs max-w-[320px]">{row.notes_short || row.notes || "—"}</td>
                  <td className="px-3 py-3"><button onClick={() => retryCreditNote(row)} disabled={retryingReturnId === row.return_id} className="bg-amber-700 hover:bg-amber-600 disabled:opacity-50 text-white px-2 py-1 rounded-lg text-xs whitespace-nowrap">{retryingReturnId === row.return_id ? 'A emitir…' : 'Emitir NC Vendus'}</button></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Tabela */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={selected.length === sales.length && sales.length > 0} onChange={selectAll} className="w-4 h-4 accent-green-500" />
            <span className="text-gray-400 text-sm">
              {sales.length} vendas pendentes de documento Vendus {viewMode === "all_pending" ? "(todos os dias)" : `(dia ${dateFilter})`}
            </span>
          </div>
          {selected.length > 0 && <div className="flex flex-wrap items-center gap-2">
            {activeRegister && <button onClick={fiscalizeBatch} disabled={fiscalizing || excludingSelected} className="bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-2"><Send className="w-3.5 h-3.5" />{fiscalizing ? "A enviar..." : `Emitir ${selected.length} faturas no Vendus`}</button>}
            <button onClick={excludeSelectedFromVendus} disabled={excludingSelected || fiscalizing} className="bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-2"><Trash2 className="w-3.5 h-3.5" />{excludingSelected ? 'A excluir…' : `Excluir ${selected.length} selecionada(s)`}</button>
          </div>}
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="w-10 px-4 py-3"></th>
              {["Hora","NCP","Cliente","NIF","Artigos","Total","Pagamento","Estado","Ação"].map(h => (
                <th key={h} className="text-left text-gray-400 text-xs font-medium px-3 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="text-center text-gray-500 py-8 text-sm">A carregar...</td>
              </tr>
            ) : sales.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center py-10">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Nenhuma venda pendente de documento Vendus para este dia</p>
                </td>
              </tr>
            ) : (
              Object.entries(groupedSales)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .flatMap(([day, daySales]) => ([
                  <tr key={`group-${day}`} className="bg-gray-900/70 border-b border-gray-700">
                    <td colSpan={10} className="px-4 py-2 text-xs font-semibold text-green-400">
                      Dia {day}
                    </td>
                  </tr>,
                  ...daySales.map((sale) => (
                    <tr
                      key={sale.id}
                      className={`border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors ${selected.includes(sale.id) ? "bg-green-900/10" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.includes(sale.id)}
                          onChange={() => toggleSelect(sale.id)}
                          className="w-4 h-4 accent-green-500"
                        />
                      </td>
                      <td className="px-3 py-3 text-gray-400 text-sm">{sale.completed_at?.slice(11,16) || "—"}</td>
                      <td className="px-3 py-3 text-white text-sm font-medium">{sale.ncp_number || "—"}</td>
                      <td className="px-3 py-3 text-gray-300 text-sm">
                        <div>{sale.day_close_fiscal_override?.customer_name || sale.customer_name || "Consumidor Final"}</div>
                        {sale.day_close_fiscal_override && <div className="text-[10px] text-blue-300">Dados para Vendus</div>}
                      </td>
                      <td className="px-3 py-3 text-gray-400 text-sm">
                        {sale.day_close_fiscal_override?.customer_nif || "Sem NIF"}
                      </td>
                      <td className="px-3 py-3 text-gray-400 text-sm">{sale.items?.length || 0} art.</td>
                      <td className="px-3 py-3 text-white text-sm font-bold">
                        <div>{(sale.total || 0).toFixed(2)}€</div>
                        {sale.day_close_fiscal_override && (
                          <div className="text-[11px] font-normal text-blue-300 whitespace-nowrap">
                            Vendus: {(sale.day_close_fiscal_override.total || 0).toFixed(2)}€
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-gray-400 text-sm">{PAY_METHODS[sale.payment_method] || sale.payment_method}</td>
                      <td className="px-3 py-3">
                        {String(sale.invoice_type || '').toLowerCase() === 'fiscal' && !sale.vendus_invoice_id && (
                          <span className="text-xs bg-amber-900/40 text-amber-300 border border-amber-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                            Fiscal pendente / repetir
                          </span>
                        )}
                        {String(sale.invoice_type || '').toLowerCase() === 'internal' && (
                          <span className="text-xs bg-slate-700 text-slate-300 border border-slate-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                            Interna — por faturar
                          </span>
                        )}
                        {(sale.day_close_edited || sale.day_close_edited_at) && (
                          <span className="text-xs bg-blue-900/40 text-blue-400 border border-blue-800 px-2 py-0.5 rounded-full">
                            Editada
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEdit(sale)}
                            className="bg-blue-700 hover:bg-blue-600 text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1"
                          >
                            <Edit2 className="w-3 h-3" /> Editar
                          </button>
                          <button
                            onClick={() => excludeFromVendus(sale)}
                            className="bg-red-700 hover:bg-red-600 text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ]))
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <p className="text-gray-400 text-xs"><strong className="text-gray-300">Anti-duplicação:</strong> Estas vendas já estão nos mapas de vendas. Ao faturar ou repetir uma tentativa no Vendus é apenas emitido o documento fiscal — os valores <strong className="text-gray-300">não são somados novamente</strong> aos relatórios.</p>
      </div>

      {showExcludedSales && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={() => setShowExcludedSales(false)}>
          <div className="bg-slate-950 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-800 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><Archive className="w-5 h-5 text-amber-400" /> Vendas excluídas</h2>
                <p className="text-sm text-slate-400 mt-1">Podem ser consultadas e recuperadas durante 7 dias após a exclusão.</p>
              </div>
              <button onClick={() => setShowExcludedSales(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-3">
              {excludedSales.length === 0 ? (
                <div className="py-12 text-center"><CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-3" /><p className="text-slate-300">Não existem vendas recuperáveis.</p><p className="text-xs text-slate-500 mt-1">As exclusões com mais de 7 dias deixam de ser apresentadas.</p></div>
              ) : excludedSales.map(sale => {
                const expanded = expandedExcludedId === sale.id;
                const fiscalData = sale.day_close_fiscal_override || sale;
                return (
                  <div key={sale.id} className="border border-slate-700 bg-slate-900 rounded-xl overflow-hidden">
                    <button onClick={() => setExpandedExcludedId(expanded ? "" : sale.id)} className="w-full p-4 text-left hover:bg-slate-800/70 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div><p className="font-semibold text-white">{sale.ncp_number || "Venda interna"}</p><p className="text-sm text-slate-400">{fiscalData.customer_name || "Consumidor Final"} · {String(sale.completed_at || "").slice(0, 16).replace("T", " ")}</p></div>
                        <div className="md:text-right"><p className="text-lg font-bold text-white">{Number(sale.total || 0).toFixed(2)}€</p><p className="text-xs text-amber-300">Recuperável por {exclusionTimeLeft(sale)}</p></div>
                      </div>
                    </button>
                    {expanded && (
                      <div className="border-t border-slate-700 p-4 space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div><p className="text-xs text-slate-500">NIF para Vendus</p><p className="text-slate-200">{fiscalData.customer_nif || "Sem NIF"}</p></div>
                          <div><p className="text-xs text-slate-500">Pagamento</p><p className="text-slate-200">{PAY_METHODS[fiscalData.payment_method] || fiscalData.payment_method || "—"}</p></div>
                          <div><p className="text-xs text-slate-500">Excluída em</p><p className="text-slate-200">{String(sale.exclude_from_vendus_at || "").slice(0, 16).replace("T", " ")}</p></div>
                          <div><p className="text-xs text-slate-500">Limite de recuperação</p><p className="text-slate-200">{String(sale.recoverable_until || "").slice(0, 16).replace("T", " ")}</p></div>
                        </div>
                        <div className="border border-slate-700 rounded-lg overflow-hidden">
                          {(fiscalData.items || []).map((item, index) => (
                            <div key={`${item.product_id || item.name}-${index}`} className="px-3 py-2 border-b last:border-b-0 border-slate-700 flex items-start justify-between gap-3 text-sm">
                              <div><p className="text-slate-200">{item.product_name || item.name || "Artigo"}</p><p className="text-xs text-slate-500">{Number(item.quantity || 0)} × {Number(item.unit_price || 0).toFixed(2)}€{Number(item.discount_percent || item.discount || 0) ? ` · desc. ${Number(item.discount_percent || item.discount)}%` : ""}</p></div>
                              <p className="text-white font-semibold whitespace-nowrap">{Number(item.total ?? (Number(item.quantity || 0) * Number(item.unit_price || 0))).toFixed(2)}€</p>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-end">
                          <button onClick={() => restoreExcludedSale(sale)} disabled={restoringExcludedId === sale.id} className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                            <RotateCcw className="w-4 h-4" /> {restoringExcludedId === sale.id ? "A recuperar…" : "Recuperar para o Fecho do Dia"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modal Edição */}
      {showConsolidatedClose && consolidatedForm && consolidatedCash && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={() => !closingConsolidated && setShowConsolidatedClose(false)}>
          <div className="bg-slate-950 border border-slate-700 rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-slate-950 border-b border-slate-800 p-5 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Fecho consolidado · {dateFilter}</h2>
                <p className="text-sm text-slate-400">Confere todas as caixas, TPAs e restantes meios antes de encerrar.</p>
              </div>
              <button disabled={closingConsolidated} onClick={() => setShowConsolidatedClose(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-6">
              <section>
                <h3 className="text-white font-semibold mb-3">Caixas incluídas</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(consolidatedCash.sessions || []).map(session => (
                      <div key={session.open_id} className="bg-slate-900 border border-slate-700 rounded-xl p-4">
                        <div className="flex justify-between gap-3 mb-3">
                          <div><p className="text-white font-semibold">{session.operator}</p><p className="text-xs text-slate-400">{session.sales_count} venda(s) · {Number(session.sales_total || 0).toFixed(2)}€</p></div>
                          <div className="text-right"><p className="text-xs text-slate-400">Esperado</p><p className="text-white font-bold">{Number(session.expected_cash || 0).toFixed(2)}€</p></div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs text-slate-400">
                          <p>Abertura: <span className="text-slate-200 font-semibold">{Number(session.opening || 0).toFixed(2)}€</span></p>
                          <p>Entradas: <span className="text-emerald-300 font-semibold">{Number(session.summary?.cash_in || 0).toFixed(2)}€</span></p>
                          <p>Saídas: <span className="text-amber-300 font-semibold">{Number(session.summary?.cash_out || 0).toFixed(2)}€</span></p>
                        </div>
                      </div>
                  ))}
                </div>
              </section>

              <section className="bg-emerald-950/30 border border-emerald-700/60 rounded-xl p-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 text-sm">
                  <div><p className="text-slate-400">Aberturas</p><p className="text-white font-bold">{Number(consolidatedCash.totals?.opening || 0).toFixed(2)}€</p></div>
                  <div><p className="text-slate-400">Vendas</p><p className="text-white font-bold">{Number(consolidatedCash.totals?.total || 0).toFixed(2)}€</p></div>
                  <div><p className="text-slate-400">Entradas caixa</p><p className="text-white font-bold">{Number(consolidatedCash.totals?.cash_in || 0).toFixed(2)}€</p></div>
                  <div><p className="text-slate-400">Saídas caixa</p><p className="text-white font-bold">{Number(consolidatedCash.totals?.cash_out || 0).toFixed(2)}€</p></div>
                  <div><p className="text-slate-400">Dinheiro esperado</p><p className="text-emerald-300 font-bold">{Number(consolidatedPreview?.expectedByMethod?.cash || 0).toFixed(2)}€</p></div>
                </div>
                <label className="text-sm font-semibold text-white">Dinheiro total contado — conjunto de todas as caixas</label>
                <input type="number" min="0" step="0.01" value={consolidatedForm.counted_cash_total ?? ''} onChange={e => setConsolidatedForm(form => ({ ...form, counted_cash_total: e.target.value }))} className="mt-2 w-full bg-white text-slate-950 rounded-lg px-4 py-3 text-right text-xl font-bold" />
                <p className={`mt-2 text-sm font-bold ${Math.abs(consolidatedPreview?.comparison?.cash?.difference || 0) < 0.005 ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {Math.abs(consolidatedPreview?.comparison?.cash?.difference || 0) < 0.005 ? 'Dinheiro certo' : `${(consolidatedPreview?.comparison?.cash?.difference || 0) > 0 ? 'Sobra' : 'Falta'} ${Math.abs(consolidatedPreview?.comparison?.cash?.difference || 0).toFixed(2)}€ em dinheiro`}
                </p>
              </section>

              <section className="bg-slate-900 border border-slate-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3"><div><h3 className="text-white font-semibold">Terminais Multibanco/Cartão</h3><p className="text-xs text-slate-400">Esperado: {Number(consolidatedCash.expected_receipts?.card_total || 0).toFixed(2)}€ · Real: {Number(consolidatedPreview?.actualByMethod?.card_total || 0).toFixed(2)}€</p><p className={`text-xs font-bold ${Math.abs(consolidatedPreview?.comparison?.card_total?.difference || 0) < 0.005 ? 'text-emerald-400' : 'text-rose-400'}`}>{Math.abs(consolidatedPreview?.comparison?.card_total?.difference || 0) < 0.005 ? 'Multibanco certo' : `${(consolidatedPreview?.comparison?.card_total?.difference || 0) > 0 ? 'Sobra' : 'Falta'} ${Math.abs(consolidatedPreview?.comparison?.card_total?.difference || 0).toFixed(2)}€`}</p></div><button type="button" onClick={() => setConsolidatedForm(form => ({ ...form, tpas: [...form.tpas, { name: `TPA ${form.tpas.length + 1}`, amount: '0.00' }] }))} className="px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> TPA</button></div>
                <div className="space-y-2">
                  {consolidatedForm.tpas.map((tpa, index) => (
                    <div key={index} className="grid grid-cols-[1fr_150px_40px] gap-2">
                      <input value={tpa.name} onChange={e => setConsolidatedForm(form => ({ ...form, tpas: form.tpas.map((row, i) => i === index ? { ...row, name: e.target.value } : row) }))} className="bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2" />
                      <input type="number" min="0" step="0.01" value={tpa.amount} onChange={e => setConsolidatedForm(form => ({ ...form, tpas: form.tpas.map((row, i) => i === index ? { ...row, amount: e.target.value } : row) }))} className="bg-white text-slate-950 rounded-lg px-3 py-2 text-right font-bold" />
                      <button disabled={consolidatedForm.tpas.length === 1} onClick={() => setConsolidatedForm(form => ({ ...form, tpas: form.tpas.filter((_, i) => i !== index) }))} className="text-rose-400 disabled:opacity-30"><Trash2 className="w-4 h-4 mx-auto" /></button>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="text-white font-semibold mb-3">Outros meios e valores contabilísticos</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[['mbway','MBWay'],['transfer','Transferência'],['voucher','Vales/Vouchers'],['account_current','Conta corrente'],['other','Outros']].map(([key,label]) => {
                    const expected = Number(consolidatedCash.expected_receipts?.[key] || 0);
                    const actual = Number(String(consolidatedForm.actual_methods?.[key] || 0).replace(',', '.')) || 0;
                    const diff = actual - expected;
                    return <div key={key} className="bg-slate-900 border border-slate-700 rounded-xl p-3"><div className="flex justify-between"><label className="text-sm text-slate-200">{label}</label><span className="text-xs text-slate-400">Esp. {expected.toFixed(2)}€</span></div><input type="number" min="0" step="0.01" value={consolidatedForm.actual_methods?.[key] ?? ''} onChange={e => setConsolidatedForm(form => ({ ...form, actual_methods: { ...form.actual_methods, [key]: e.target.value } }))} className="mt-2 w-full bg-white text-slate-950 rounded-lg px-3 py-2 text-right font-bold" /><p className={`mt-1 text-xs ${Math.abs(diff) < 0.005 ? 'text-emerald-400' : 'text-rose-400'}`}>{Math.abs(diff) < 0.005 ? 'Certo' : `${diff > 0 ? 'Sobra' : 'Falta'} ${Math.abs(diff).toFixed(2)}€`}</p></div>;
                  })}
                </div>
              </section>

              <div className="bg-amber-950/30 border border-amber-700/60 rounded-xl p-4 text-sm text-amber-100">
                Pré-pagamentos recebidos no dia: <strong>{Number(consolidatedCash.prepayments?.received_total || 0).toFixed(2)}€</strong>. Estes valores são conciliados no respetivo meio de pagamento e permanecem separados das vendas para não haver duplicação.
              </div>
              <section className={`rounded-xl border p-5 ${Math.abs(consolidatedPreview?.totalDifference || 0) < 0.005 ? 'bg-emerald-950/50 border-emerald-600' : 'bg-rose-950/40 border-rose-600'}`}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div><p className="text-sm text-slate-300">Resultado global — compensa correções entre dinheiro e Multibanco</p><p className="text-xs text-slate-400">As diferenças por meio continuam visíveis para auditoria.</p></div>
                  <p className={`text-2xl font-black ${Math.abs(consolidatedPreview?.totalDifference || 0) < 0.005 ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {Math.abs(consolidatedPreview?.totalDifference || 0) < 0.005 ? 'CAIXA CERTA' : `${(consolidatedPreview?.totalDifference || 0) > 0 ? 'SOBRA' : 'FALTA'} ${Math.abs(consolidatedPreview?.totalDifference || 0).toFixed(2)}€`}
                  </p>
                </div>
              </section>
              <textarea value={consolidatedForm.note} onChange={e => setConsolidatedForm(form => ({ ...form, note: e.target.value }))} placeholder="Observações do fecho consolidado (opcional)" className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-3 py-3" rows={2} />
            </div>

            <div className="sticky bottom-0 bg-slate-950 border-t border-slate-800 p-5 flex justify-end gap-3">
              <button disabled={closingConsolidated} onClick={() => setShowConsolidatedClose(false)} className="px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl">Cancelar</button>
              <button disabled={closingConsolidated} onClick={closeConsolidatedCash} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-xl">{closingConsolidated ? 'A fechar…' : `Confirmar e fechar ${consolidatedCash.session_count} caixas`}</button>
            </div>
          </div>
        </div>
      )}

      {editSale && editForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <div>
                <h2 className="text-lg font-bold text-white">Editar Venda {editSale.ncp_number}</h2>
                <p className="text-gray-400 text-xs mt-0.5">Dados apenas para o documento Vendus — a venda interna original não é alterada</p>
              </div>
              <button onClick={() => setEditSale(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">

              {/* Cliente */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Nome Cliente</label>
                  <input value={editForm.customer_name} onChange={e => setEditForm(f => ({ ...f, customer_name: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">NIF Cliente</label>
                  <input value={editForm.customer_nif} onChange={e => setEditForm(f => ({ ...f, customer_nif: e.target.value }))}
                    inputMode="numeric" maxLength={11} placeholder="999999990" className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Método Pagamento</label>
                  <select value={editForm.payment_method} onChange={e => setEditForm(f => ({ ...f, payment_method: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600">
                    {Object.entries(PAY_METHODS).filter(([k]) => k !== 'multibanco').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Notas</label>
                  <input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                </div>
              </div>
              <p className="text-[11px] text-gray-400 -mt-3">
                Para identificar o cliente no documento Vendus, indique nome e NIF. Sem NIF, o Vendus emite para Consumidor Final.
              </p>

              {/* Itens */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-gray-400 text-xs">Artigos</label>
                </div>
                <div className="space-y-2">
                  {editForm.items.map((item, idx) => (
                    <div key={idx} className="bg-gray-800 rounded-xl p-3 grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <p className="text-white text-xs font-medium truncate">{item.product_name}</p>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-gray-500 text-xs mb-0.5">Qtd</label>
                        <input type="number" step="1" min="1" value={item.quantity}
                          onChange={e => {
                            const items = [...editForm.items];
                            items[idx] = { ...items[idx], quantity: parseInt(e.target.value, 10) || 1 };
                            setEditForm(f => ({ ...f, items }));
                          }}
                          className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1 text-xs focus:outline-none" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-gray-500 text-xs mb-0.5">Preço €</label>
                        <input type="number" step="0.01" value={item.unit_price}
                          onChange={e => {
                            const items = [...editForm.items];
                            items[idx] = { ...items[idx], unit_price: parseFloat(String(e.target.value).replace(',', '.')) || 0 };
                            setEditForm(f => ({ ...f, items }));
                          }}
                          className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1 text-xs focus:outline-none" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-gray-500 text-xs mb-0.5">Desc %</label>
                        <input type="number" step="1" min="0" max="100" value={item.discount_percent || 0}
                          onChange={e => {
                            const items = [...editForm.items];
                            items[idx] = { ...items[idx], discount_percent: parseFloat(String(e.target.value).replace(',', '.')) || 0 };
                            setEditForm(f => ({ ...f, items }));
                          }}
                          className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1 text-xs focus:outline-none" />
                      </div>
                      <div className="col-span-1 text-right">
                        <p className="text-gray-500 text-xs mb-0.5">Total</p>
                        <p className="text-white text-xs font-bold">
                          {((item.quantity || 0) * (item.unit_price || 0) * (1 - (item.discount_percent || 0) / 100)).toFixed(2)}€
                        </p>
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button onClick={() => {
                          const items = editForm.items.filter((_, i) => i !== idx);
                          setEditForm(f => ({ ...f, items }));
                        }} className="text-red-400 hover:text-red-300 p-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="col-span-12 flex flex-wrap items-center gap-1.5 pt-1 border-t border-gray-700/70">
                        <span className="text-[11px] text-gray-500 mr-1">Aplicar ao preço:</span>
                        {[20, 30, 40, 45, 50].map(percent => <button key={percent} type="button" onClick={() => convertDiscountToPrice(idx, percent)} className="bg-blue-700 hover:bg-blue-600 text-white px-2 py-1 rounded text-[11px] font-semibold">{percent}%</button>)}
                        {Number(item.discount_percent || 0) > 0 && <button type="button" onClick={() => convertDiscountToPrice(idx)} className="bg-emerald-700 hover:bg-emerald-600 text-white px-2 py-1 rounded text-[11px] font-semibold">Colocar este valor no preço do artigo</button>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totais recalculados */}
                {(() => {
                  const t = recalcTotals(editForm.items);
                  return (
                    <div className="mt-3 bg-gray-800 rounded-xl p-3 flex justify-end gap-6 text-sm">
                      <span className="text-gray-400">Subtotal: <span className="text-white font-medium">{t.subtotal.toFixed(2)}€</span></span>
                      <span className="text-gray-400">IVA: <span className="text-white font-medium">{t.vat_total.toFixed(2)}€</span></span>
                      <span className="text-gray-400">Total: <span className="text-green-400 font-bold text-base">{t.total.toFixed(2)}€</span></span>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-800">
              <button onClick={() => setEditSale(null)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl text-sm">Cancelar</button>
              <button onClick={saveEdit} disabled={savingEdit} className="flex-1 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm flex items-center justify-center gap-2">
                <Check className="w-4 h-4" /> {savingEdit ? 'A guardar…' : 'Guardar Alterações'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
